/**
 * Phase 129 (Plan 129-10, D-15) — frontend client for the project Engineering tab's release flags.
 *
 * Endpoints (flag-gated and team-gated server-side; each scopes the key / ticket to the project):
 *   GET    /api/projects/:projectId/engineering/release-flags
 *   POST   /api/projects/:projectId/engineering/release-flags/:key/switch-off
 *   DELETE /api/projects/:projectId/engineering/release-flags/:key/override
 *   POST   /api/projects/:projectId/engineering/release-flags/tickets/:ticket/verify
 *
 * The row and result shapes are the admin ones (`services/api/releaseFlags`).
 */
import { apiDelete, apiGet, apiPost } from './client';
import type {
  AdminReleaseFlagRow,
  ReleaseFlagStateResult,
  ReleaseFlagSwitchOnResult,
} from './releaseFlags';

const base = (projectId: number): string => `/projects/${projectId}/engineering/release-flags`;

/** Only flags whose ticket resolves to exactly one task of this project. */
export async function fetchProjectReleaseFlags(projectId: number): Promise<AdminReleaseFlagRow[]> {
  const res = await apiGet<{ data: AdminReleaseFlagRow[] }>(base(projectId));
  return res.data;
}

/** Explicit `enabled:false` override, audited. */
export async function switchProjectReleaseFlagOff(
  projectId: number,
  key: string,
): Promise<ReleaseFlagStateResult> {
  const res = await apiPost<{ data: ReleaseFlagStateResult }>(
    `${base(projectId)}/${encodeURIComponent(key)}/switch-off`,
  );
  return res.data;
}

/** Back to verified / default (idempotent). */
export async function resetProjectReleaseFlagOverride(
  projectId: number,
  key: string,
): Promise<ReleaseFlagStateResult> {
  const res = await apiDelete<{ data: ReleaseFlagStateResult }>(
    `${base(projectId)}/${encodeURIComponent(key)}/override`,
  );
  return res.data;
}

/** "Switch on (mark verified)" for one of the project's tickets (one-way). */
export async function switchProjectReleaseFlagTicketOn(
  projectId: number,
  ticket: string,
): Promise<ReleaseFlagSwitchOnResult> {
  const res = await apiPost<{ data: ReleaseFlagSwitchOnResult }>(
    `${base(projectId)}/tickets/${encodeURIComponent(ticket)}/verify`,
  );
  return res.data;
}
